import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, CheckCircleIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { createBucketIfNotExists, uploadFile } from '../../lib/storage';
import { sendConfirmationEmail } from '../../lib/email';
import { toast } from 'react-hot-toast';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  tournamentId: string;
  firstName: string;
  lastName: string;
  sex: string;
  category: string;
  residentClub: string;
  handicap: string;
  shirtSize: string;
  phone: string;
  email: string;
  transferDate: string;
  transferAmount: string;
  senderName: string;
}

const emptyForm = (): FormState => ({
  tournamentId: '',
  firstName: '',
  lastName: '',
  sex: '',
  category: '',
  residentClub: '',
  handicap: '',
  shirtSize: '',
  phone: '',
  email: '',
  transferDate: '',
  transferAmount: '',
  senderName: '',
});

export default function MobilePlayerRegistrationSheet({ isOpen, onClose }: Props) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [tournaments, setTournaments] = useState<{ id: string; name: string }[]>([]);
  const [bankInfo, setBankInfo] = useState<{
    bank_name: string;
    account_name: string;
    account_number: string;
    sort_code?: string;
  } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tournamentCategories, setTournamentCategories] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm(emptyForm());
      setSelectedFile(null);
      setError('');
      setRegistrationNumber('');
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const { data: regSetting } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'registration_enabled')
        .maybeSingle();

      if (regSetting) {
        const enabled = typeof regSetting.value === 'boolean'
          ? regSetting.value
          : regSetting.value === 'true' || regSetting.value === true;
        setRegistrationEnabled(enabled);
      }

      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('id, name')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true });

      const list = tournamentData || [];
      setTournaments(list);
      if (list.length > 0) {
        setForm(prev => ({ ...prev, tournamentId: list[0].id }));
        fetchCategoriesForTournament(list[0].id);
      }

      const { data: bankData } = await supabase
        .from('bank_information')
        .select('*')
        .eq('is_primary', true)
        .eq('is_public', true)
        .maybeSingle();

      if (bankData) {
        setBankInfo(bankData);
      } else {
        const { data: anyBank } = await supabase
          .from('bank_information')
          .select('*')
          .eq('is_public', true)
          .limit(1)
          .maybeSingle();
        if (anyBank) setBankInfo(anyBank);
      }

      await createBucketIfNotExists('paymentproofs', true);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoriesForTournament = async (tournamentId: string) => {
    const { data } = await supabase
      .from('tournament_categories')
      .select('id, name')
      .eq('tournament_id', tournamentId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    setTournamentCategories(data || []);
  };

  const handleNext = () => {
    const { firstName, lastName, sex, category, residentClub, handicap, shirtSize, phone, email, tournamentId } = form;
    if (!firstName || !lastName || !sex || !category || !residentClub || !handicap || !shirtSize || !phone || !email || !tournamentId) {
      setError('Please fill in all required fields');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!registrationEnabled) {
      setError('Registration is currently disabled');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    const { transferDate, senderName } = form;
    if (!transferDate || !senderName) {
      setError('Please fill in all payment details');
      return;
    }
    if (!selectedFile) {
      setError('Please upload your proof of payment');
      return;
    }
    try {
      setIsSubmitting(true);
      setError('');

      const regNumber = `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

      const paymentProofUrl = await uploadFile(selectedFile, 'paymentproofs', 'players');

      const { error: insertError } = await supabase.from('players').insert([{
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        country: 'Nigeria',
        resident_club: form.residentClub,
        handicap: parseInt(form.handicap),
        category: form.category,
        tournament_id: form.tournamentId,
        gender: form.sex,
        registration_status: { personalInfo: true, payment: false, verification: false },
        status: 'pending',
        payment_proof_url: paymentProofUrl,
        shirt_size: form.shirtSize,
        registration_number: regNumber,
      }]);

      if (insertError) throw insertError;

      await sendConfirmationEmail(form.email, form.firstName, regNumber, form.shirtSize, form.lastName);

      setRegistrationNumber(regNumber);
      setStep(3);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('File must be under 10MB'); return; }
    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) { setError('File must be JPEG, PNG, or PDF'); return; }
    setSelectedFile(file);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={step < 3 ? onClose : undefined} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[94vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">
              {step === 3 ? 'Registration Complete' : 'Register Player'}
            </h2>
            {step < 3 && (
              <p className="text-xs text-gray-400 mt-0.5">Step {step} of 2 — {step === 1 ? 'Personal Info' : 'Payment'}</p>
            )}
          </div>
          {step < 3 && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {/* Step indicators */}
        {step < 3 && (
          <div className="flex gap-1.5 px-5 pt-3 pb-1 flex-shrink-0">
            {[1, 2].map(s => (
              <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-golf-orange' : 'bg-gray-200'}`} />
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
            </div>
          ) : step === 3 ? (
            <div className="flex flex-col items-center text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-golf-gray">Registration Successful!</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {form.firstName} {form.lastName} has been registered.
                </p>
              </div>
              <div className="bg-golf-sand/40 rounded-2xl px-6 py-4 w-full">
                <p className="text-xs text-gray-500 mb-1">Registration Number</p>
                <p className="text-lg font-bold text-golf-gray tracking-wider">{registrationNumber}</p>
              </div>
              <p className="text-xs text-gray-400">A confirmation email has been sent to {form.email}</p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-golf-orange text-white font-semibold text-sm mt-2"
              >
                Done
              </button>
            </div>
          ) : step === 1 ? (
            <div className="space-y-4">
              {!registrationEnabled && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-600 font-medium">Registration is currently disabled</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Tournament</label>
                <select
                  value={form.tournamentId}
                  onChange={e => {
                    const tid = e.target.value;
                    setForm(prev => ({ ...prev, tournamentId: tid, category: '' }));
                    if (tid) fetchCategoriesForTournament(tid);
                    else setTournamentCategories([]);
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                >
                  {tournaments.length === 0 && <option value="">No upcoming tournaments</option>}
                  {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">First Name</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Last Name</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Sex</label>
                  <select
                    value={form.sex}
                    onChange={e => setForm(prev => ({ ...prev, sex: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Shirt Size</label>
                  <select
                    value={form.shirtSize}
                    onChange={e => setForm(prev => ({ ...prev, shirtSize: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                  >
                    <option value="">Select</option>
                    {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Playing Category</label>
                <select
                  value={form.category}
                  onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                >
                  <option value="">Select category</option>
                  {tournamentCategories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
                {tournamentCategories.length === 0 && form.tournamentId && (
                  <p className="mt-1 text-xs text-gray-400">No categories defined yet.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Resident Club</label>
                <input
                  type="text"
                  value={form.residentClub}
                  onChange={e => setForm(prev => ({ ...prev, residentClub: e.target.value }))}
                  placeholder="Golf club name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Handicap</label>
                <input
                  type="number"
                  value={form.handicap}
                  onChange={e => setForm(prev => ({ ...prev, handicap: e.target.value }))}
                  placeholder="e.g. 18"
                  min="0"
                  max="54"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+234 ..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div className="pb-2" />
            </div>
          ) : (
            <div className="space-y-4">
              {bankInfo && (
                <div className="bg-golf-sand/40 border border-golf-sand rounded-xl px-4 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bank Details</p>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Bank</span>
                      <span className="font-semibold text-golf-gray">{bankInfo.bank_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Account Name</span>
                      <span className="font-semibold text-golf-gray">{bankInfo.account_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Account Number</span>
                      <span className="font-bold text-golf-gray tracking-wider">{bankInfo.account_number}</span>
                    </div>
                    {bankInfo.sort_code && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Sort Code</span>
                        <span className="font-semibold text-golf-gray">{bankInfo.sort_code}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Transfer Date</label>
                <input
                  type="date"
                  value={form.transferDate}
                  onChange={e => setForm(prev => ({ ...prev, transferDate: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Sender Name</label>
                <input
                  type="text"
                  value={form.senderName}
                  onChange={e => setForm(prev => ({ ...prev, senderName: e.target.value }))}
                  placeholder="Name on bank account"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Proof of Payment</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full py-4 border-2 border-dashed rounded-xl flex flex-col items-center gap-2 transition-colors ${
                    selectedFile ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  {selectedFile ? (
                    <>
                      <CheckCircleIcon className="w-6 h-6 text-green-600" />
                      <p className="text-sm font-medium text-green-700 truncate max-w-[220px]">{selectedFile.name}</p>
                      <p className="text-xs text-green-500">Tap to change</p>
                    </>
                  ) : (
                    <>
                      <DocumentArrowUpIcon className="w-6 h-6 text-gray-400" />
                      <p className="text-sm text-gray-500">Tap to upload</p>
                      <p className="text-xs text-gray-400">JPEG, PNG or PDF — max 10MB</p>
                    </>
                  )}
                </button>
              </div>

              <div className="pb-2" />
            </div>
          )}

          {error && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {step < 3 && (
          <div className="flex gap-3 px-5 pt-4 pb-20 border-t border-gray-100 flex-shrink-0">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50"
              >
                Back
              </button>
            )}
            {step === 1 ? (
              <button
                onClick={handleNext}
                disabled={!registrationEnabled}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
              >
                Next: Payment
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
              >
                {isSubmitting ? 'Registering...' : 'Submit Registration'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
